import React, { useMemo } from 'react'
import { AlertOctagon, LogOut, MessageCircle, Phone } from 'lucide-react'
import {
  SUPPORT_MOBILE,
  buildWhatsAppUrl,
  clearManagementCache,
  readCachedPortal,
  telSupportHref,
} from '../../services/managementService'

function formatMoney(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function SuspendedSadFace() {
  return (
    <div className="suspend-face-wrap" aria-hidden>
      <svg
        className="suspend-face"
        viewBox="0 0 120 120"
        width="96"
        height="96"
        role="img"
      >
        <defs>
          <radialGradient id="suspendFaceFill" cx="40%" cy="35%" r="65%">
            <stop offset="0%" stopColor="#fed7aa" />
            <stop offset="55%" stopColor="#fb923c" />
            <stop offset="100%" stopColor="#ea580c" />
          </radialGradient>
          <linearGradient id="suspendTearFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#93c5fd" />
            <stop offset="100%" stopColor="#3b82f6" />
          </linearGradient>
        </defs>

        {/* soft glow */}
        <circle className="suspend-face-glow" cx="60" cy="60" r="52" fill="#fed7aa" opacity="0.55" />

        {/* head */}
        <circle cx="60" cy="58" r="38" fill="url(#suspendFaceFill)" />
        <circle cx="60" cy="58" r="38" fill="none" stroke="#c2410c" strokeWidth="2.5" opacity="0.35" />

        {/* eyebrows — sad tilt */}
        <path
          className="suspend-brow suspend-brow--left"
          d="M38 42 Q46 36 54 44"
          fill="none"
          stroke="#9a3412"
          strokeWidth="3.2"
          strokeLinecap="round"
        />
        <path
          className="suspend-brow suspend-brow--right"
          d="M66 44 Q74 36 82 42"
          fill="none"
          stroke="#9a3412"
          strokeWidth="3.2"
          strokeLinecap="round"
        />

        {/* eyes */}
        <g className="suspend-eyes">
          <ellipse className="suspend-eye" cx="46" cy="54" rx="5.5" ry="6.5" fill="#9a3412" />
          <ellipse className="suspend-eye" cx="74" cy="54" rx="5.5" ry="6.5" fill="#9a3412" />
          <circle cx="47.5" cy="52.5" r="1.6" fill="#ffedd5" opacity="0.9" />
          <circle cx="75.5" cy="52.5" r="1.6" fill="#ffedd5" opacity="0.9" />
        </g>

        {/* sad mouth */}
        <path
          className="suspend-mouth"
          d="M44 78 Q60 66 76 78"
          fill="none"
          stroke="#9a3412"
          strokeWidth="3.5"
          strokeLinecap="round"
        />

        {/* tear */}
        <g className="suspend-tear">
          <path
            d="M82 62 C82 62 76 70 82 78 C88 70 82 62 82 62 Z"
            fill="url(#suspendTearFill)"
            opacity="0.95"
          />
        </g>
      </svg>
    </div>
  )
}

function AccountSuspended() {
  const portal = useMemo(() => readCachedPortal(), [])
  const isAdminSuspended = Boolean(portal?.IsAccountSuspended)
  const suspendReason = (portal?.SuspendReason || portal?.Message || '').trim()
  const overdueCount = portal?.OverdueCount || portal?.OverduePayments?.length || 0
  const overdueAmount = portal?.OverdueAmount || 0

  const companyName =
    portal?.CustomerName ||
    (() => {
      try {
        return JSON.parse(localStorage.getItem('companyProfile') || '{}').Name || 'Your company'
      } catch {
        return 'Your company'
      }
    })()

  const waUrl = buildWhatsAppUrl({
    customerName: companyName,
    registerCode: portal?.Code,
    reason: isAdminSuspended
      ? 'Our software account was suspended by Ainsoft.'
      : 'Our software account is suspended due to overdue payments.',
    amount: isAdminSuspended ? undefined : overdueAmount,
    details: isAdminSuspended
      ? suspendReason || 'Please help restore access.'
      : overdueCount
        ? `We have ${overdueCount} overdue payment(s) totaling ${formatMoney(overdueAmount)}.`
        : undefined,
  })

  const handleLogout = () => {
    clearManagementCache()
    localStorage.removeItem('authToken')
    localStorage.removeItem('userInfo')
    localStorage.removeItem('companyProfile')
    window.location.href = '/login'
  }

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden">
      <style>{`
        .suspend-face-wrap {
          display: flex;
          justify-content: center;
          margin: 0 auto 1.25rem;
          width: 7rem;
          height: 7rem;
          border-radius: 1.5rem;
          background: linear-gradient(160deg, #ffedd5 0%, #fff 70%);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.8), 0 10px 30px rgba(234, 88, 12, 0.14);
          align-items: center;
        }
        .suspend-face {
          animation: suspend-bob 2.8s ease-in-out infinite;
          transform-origin: 50% 60%;
        }
        .suspend-face-glow {
          animation: suspend-pulse 2.4s ease-in-out infinite;
          transform-origin: center;
        }
        .suspend-eyes {
          animation: suspend-blink 4.2s ease-in-out infinite;
          transform-origin: 60px 54px;
        }
        .suspend-brow--left {
          animation: suspend-brow-left 2.8s ease-in-out infinite;
          transform-origin: 46px 42px;
        }
        .suspend-brow--right {
          animation: suspend-brow-right 2.8s ease-in-out infinite;
          transform-origin: 74px 42px;
        }
        .suspend-mouth {
          animation: suspend-frown 2.8s ease-in-out infinite;
          transform-origin: 60px 72px;
        }
        .suspend-tear {
          animation: suspend-tear-fall 2.6s ease-in infinite;
          transform-origin: 82px 62px;
        }
        @keyframes suspend-bob {
          0%, 100% { transform: translateY(0) rotate(-2deg); }
          50% { transform: translateY(5px) rotate(2deg); }
        }
        @keyframes suspend-pulse {
          0%, 100% { opacity: 0.35; transform: scale(1); }
          50% { opacity: 0.55; transform: scale(1.06); }
        }
        @keyframes suspend-blink {
          0%, 42%, 46%, 100% { transform: scaleY(1); }
          44% { transform: scaleY(0.12); }
        }
        @keyframes suspend-brow-left {
          0%, 100% { transform: rotate(0deg); }
          50% { transform: rotate(-6deg); }
        }
        @keyframes suspend-brow-right {
          0%, 100% { transform: rotate(0deg); }
          50% { transform: rotate(6deg); }
        }
        @keyframes suspend-frown {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(2px); }
        }
        @keyframes suspend-tear-fall {
          0% { opacity: 0; transform: translateY(-4px) scale(0.6); }
          18% { opacity: 1; transform: translateY(0) scale(1); }
          70% { opacity: 0.9; transform: translateY(14px) scale(1); }
          100% { opacity: 0; transform: translateY(26px) scale(0.85); }
        }
        @media (prefers-reduced-motion: reduce) {
          .suspend-face,
          .suspend-face-glow,
          .suspend-eyes,
          .suspend-brow--left,
          .suspend-brow--right,
          .suspend-mouth,
          .suspend-tear {
            animation: none !important;
          }
        }
      `}</style>

      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at top, #fee2e2 0%, #f8fafc 45%, #e2e8f0 100%)',
        }}
      />
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-red-400/20 blur-3xl" />
      <div className="absolute -bottom-28 -left-20 h-80 w-80 rounded-full bg-rose-300/25 blur-3xl" />

      <div className="relative w-full max-w-lg">
        <div className="rounded-3xl bg-white/90 backdrop-blur shadow-2xl border border-white/80 overflow-hidden">
          <div className="px-8 pt-10 pb-6 text-center">
            <SuspendedSadFace />
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-red-600">
              Access restricted
            </p>
            <h1 className="mt-2 text-2xl font-bold text-slate-900 tracking-tight">
              Account suspended
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 max-w-sm mx-auto">
              {isAdminSuspended
                ? suspendReason ||
                  'This software account was suspended by the administrator. Contact Ainsoft support to restore access.'
                : 'This software is locked because license payments are overdue. Clear the outstanding balance or contact Ainsoft support to restore access.'}
            </p>
          </div>

          <div className="px-8 pb-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Account
                  </p>
                  <p className="mt-1 font-semibold text-slate-900 truncate">{companyName}</p>
                  {portal?.Code ? (
                    <p className="text-xs text-slate-500 mt-0.5">Register code · {portal.Code}</p>
                  ) : null}
                </div>
                <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700">
                  <AlertOctagon className="h-3.5 w-3.5" />
                  Suspended
                </span>
              </div>
            </div>
          </div>

          {!isAdminSuspended ? (
            <div className="px-8 pb-6">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl border border-red-100 bg-gradient-to-br from-red-50 to-white p-4 text-center">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-red-600/80">
                    Overdue items
                  </p>
                  <p className="mt-2 text-3xl font-bold tabular-nums text-red-700">{overdueCount}</p>
                </div>
                <div className="rounded-2xl border border-red-100 bg-gradient-to-br from-red-50 to-white p-4 text-center">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-red-600/80">
                    Amount due
                  </p>
                  <p className="mt-2 text-2xl font-bold tabular-nums text-red-700 leading-tight">
                    {formatMoney(overdueAmount)}
                  </p>
                </div>
              </div>
              <p className="mt-3 text-center text-xs text-slate-500">
                Payment details are available with support. Use WhatsApp or call to settle and unlock.
              </p>
            </div>
          ) : (
            <div className="px-8 pb-6">
              <p className="text-center text-xs text-slate-500">
                Contact Ainsoft support to review and restore this account.
              </p>
            </div>
          )}

          <div className="px-8 pb-8 space-y-2.5">
            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] px-4 py-3.5 text-sm font-semibold text-white shadow-sm hover:brightness-95 transition"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp support
            </a>
            <a
              href={telSupportHref()}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 transition"
            >
              <Phone className="h-4 w-4" />
              Call {SUPPORT_MOBILE}
            </a>
            <button
              type="button"
              onClick={handleLogout}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>

        <p className="mt-5 text-center text-xs text-slate-500">
          Ainsoft Delivery · billing &amp; license enforcement
        </p>
      </div>
    </div>
  )
}

export default AccountSuspended
